import React from 'react';
import Tooltip from 'apollo-react/components/Tooltip';
import Info from 'apollo-react-icons/Info';

/** Helper copy shown in the info icon tooltip (HCP signup wizard). */
export const HCP_SIGNUP_FIELD_HINTS = {
  firstName: 'Your legal first name as it appears on official ID.',
  lastName: 'Your legal last name as it appears on official ID.',
  email: 'Used for your account login and important notifications.',
  category:
    'Your professional or portal segment — same categories as standard account registration.',
  password: 'Choose a strong password. You may paste from a password manager.',
  confirmPassword: 'Enter the same password again to confirm.',
  dateOfBirth:
    'Used for eligibility and identity checks. Select from the calendar.',
  nationality: 'Country of citizenship or nationality on your passport or ID.',
  idNumber:
    'Government-issued ID number (national ID, passport, or equivalent).',
  gender: 'As recorded on your official documents, when applicable.',
  countryCode: 'International dialing code for your phone number.',
  phoneNumber: 'Local mobile number without the country code (digits only).',
  addressCountry: 'Country where you currently live or receive mail.',
  addressState: 'State, province, or region within the selected country.',
  addressCity: 'City or locality for your address.',
  addressStreet: 'Street address, including building or unit if applicable.',
  addressZipCode: 'Postal or ZIP code for your address.',
  idDocument:
    'Clear scan or photo of your ID. Allowed file types and size are shown below the field.',

  licensePrefix: 'Prefix or type code if your licensing board uses one.',
  licenseNumber: 'Full license number exactly as shown on your license.',
  issuingCountry: 'Country or region that issued this license.',
  licenseAuthorityName: 'Name of the licensing board or medical council.',
  licenseIssueDate: 'Date the license was first issued.',
  licenseExpiryDate: 'Expiry or renewal date on your current license.',
  licenseAttachment:
    'Upload a copy of your license for verification. Allowed types follow the hint below the field.',

  eduCountryOfCertificate: 'Country where this qualification was awarded.',
  eduDegreeName: 'Official qualification title (e.g. MBBS, MD, BDS).',
  eduInstitutionName:
    'University, college, or school that issued the certificate.',
  eduAuthorityName: 'Certifying or regulatory body for this qualification.',
  eduFieldOfStudy: 'Main discipline or specialty of your studies.',
  eduGraduationDate: 'Date you completed or graduated from this program.',
  eduStudyYears: 'Total years of study for this qualification (1–10).',
  eduAccreditationStatus: 'Whether the program or institution is accredited.',
  eduAccreditingAuthority:
    'Organization that accredited the program or institution.',
  eduAccreditationDocument:
    'When status is Accredited, upload proof of accreditation. Types and size are shown above the file control.',
  expInternshipTraining:
    'Whether you completed internship or supervised clinical training.',
  expPostGradInternship:
    'Whether you completed a post‑graduate internship (Yes or No) when training applies.',
  expInternshipCompletion:
    'Whether you completed the internship requirements (Yes or No). A certificate is required when this is Yes.',
  expInternshipStart: 'Start date of your internship period.',
  expInternshipEnd: 'End date of your internship period.',
  expInternshipDates:
    'The start and end dates of your supervised internship or training period.',
  expInternshipCertificate:
    'Completion certificate for your training, if required in your jurisdiction.',
  expAdditionalExperience:
    'Whether you have additional professional experience to report when training is Yes (Yes or No).',
  expAdditionalExperienceDates:
    'Start and end dates for your additional experience period when Additional experience is Yes.',
  expAdditionalExperienceCertificate:
    'Certificate or proof for the additional experience period when Additional experience is Yes.',
  expUndergraduateInternship:
    'Undergraduate internship or elective narrative (optional).',
  expUndergraduateInternshipStart:
    'Start date of your undergraduate internship period, if any.',
  expUndergraduateInternshipEnd:
    'End date of your undergraduate internship period, if any.',
  expUndergraduateInternshipCertificate:
    'Certificate for your undergraduate internship when required by your program or when you enter dates.',
  expRelevantAttachments:
    'Optional supporting files (references, certificates, etc.). PDF or images.',
};

/** Info icon with tooltip on hover. Purposely not tab-focusable so Tab moves between form inputs. */
export function HcpSignupHintIcon({ hint }) {
  if (!hint) return null;
  return (
    <Tooltip title={hint} placement="top" enterDelay={250}>
      <span
        className="hcpSignupFieldLabelHintTrigger"
        role="img"
        aria-label={hint}
      >
        <Info fontSize="extraSmall" style={{ color: '#595959' }} />
      </span>
    </Tooltip>
  );
}

/** Label text + optional required (*) after the info icon (Apollo appends * after the whole label otherwise). */
export function HcpSignupLabelWithHint({ text, hint, showRequiredAfterText }) {
  const hasHint = Boolean(hint);
  if (!hasHint && !showRequiredAfterText) return text;
  return (
    <span className="formLabelWithIcon">
      {text}
      {hasHint ? <HcpSignupHintIcon hint={hint} /> : null}
      {showRequiredAfterText ? (
        <span style={{ color: '#c00' }} aria-hidden="true">
          {' *'}
        </span>
      ) : null}
    </span>
  );
}

/** Native date row label + optional tooltip + required star (after hint icon when both apply). */
export function HcpSignupNativeDateLabel({ htmlFor, text, hint, required }) {
  return (
    <label className="authNativeDateLabel" htmlFor={htmlFor}>
      {hint ? (
        <span
          className="formLabelWithIcon"
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {text}
          <HcpSignupHintIcon hint={hint} />
          {required ? (
            <span style={{ color: '#c00' }} aria-hidden="true">
              {' *'}
            </span>
          ) : null}
        </span>
      ) : (
        <>
          {text}
          {required ? (
            <>
              {' '}
              <span style={{ color: '#c00' }}>*</span>
            </>
          ) : null}
        </>
      )}
    </label>
  );
}
